import {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { IonButton, IonIcon, IonInput, IonItem } from "@ionic/react";
import PymblePage from "./PymblePage";
import {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  setOptions,
  Eventcalendar,
  MbscEventcalendarView,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  MbscCalendarEvent,
  Datepicker,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  snackbar,
  Popup,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Button,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Input,
  Textarea,
  formatDate,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  MbscPopupButton,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  Select,
  CalendarNav,
  CalendarNext,
  CalendarPrev,
  CalendarToday,
} from "@mobiscroll/react";

import dateAndTime from "date-and-time";
import {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  RoomResourceDetail,
  useRoomResources,
} from "../hooks/useRoomResources";
import { useRoomTimetable } from "../hooks/useRoomTimetable";
import { LessonDetail, useLessonDetail } from "../hooks/useLessonDetail";
import { displayMsg } from "pymble-ui/components";
import {
  businessOutline,
  calendar,
  close,
  homeOutline,
  search,
} from "ionicons/icons";
import CoverRoomSelect from "../components/common/CoverRoomSelect";
import SelectedContext from "../contexts/SelectedContext";
import { BuildingDetail, RoomDetail } from "../hooks/useRoomList";

import "./RoomSwap.css";

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
interface RoomSwapProps { }

const RoomSwap: React.FC<RoomSwapProps> = () => {
  const { selectedAbsenceDate, setSelectedAbsenceDate } =
    useContext(SelectedContext);

  const [selectedDate, setSelectedDate] = useState(
    // dateAndTime.format(new Date(), "YYYY-MM-DD", true)
    selectedAbsenceDate
  );
  const [openPicker, setOpenPicker] = useState(false);

  const showPicker = useCallback(() => {
    setOpenPicker(true);
  }, []);
  const closePicker = useCallback(() => {
    setOpenPicker(false);
  }, []);

  /**
   * Render the calendar header. This is a custom header that includes the calendar navigation and picker.
   *
   * @returns {JSX.Element}
   */
  const myCalendarHeader = () => {
    return (
      <Fragment>
        <CalendarNav />
        <IonItem lines="none">
          <IonIcon icon={search} slot="start" />
          <IonInput
            placeholder="Search Rooms"
            className="room-swap-search"
            value={searchRoomsString}
            onIonChange={onSearchRoomsChange}
            debounce={500}
            title="Search Rooms and Buildings"
          />
          <IonButton
            fill="clear"
            shape="round"
            size="small"
            onClick={resetRoomSearch}
            slot="end"
            title="Clear search only"
            className={searchRoomsString ? "ion-text-capitalize" : "ion-hide"}
          >
            <IonIcon icon={close} />
          </IonButton>
        </IonItem>
        <div className="calendar-nav-tools">
          <CalendarPrev />
          <CalendarToday />
          <CalendarNext />
          <Datepicker
            controls={["calendar"]}
            display="anchored"
            showOnClick={false}
            showOnFocus={false}
            isOpen={openPicker}
            onClose={closePicker}
            onChange={(event: any) => {
              onSelectedDateChange({ date: event.value });
            }}
            touchUi={false}
            inputComponent="ion-input"
            inputProps={{ placeholder: "Select date", style: { width: "1px" } }}
            title="Select date"
            showEventTooltip={true}
          />
          <IonButton
            onClick={showPicker}
            size="small"
            fill="clear"
            color="secondary"
            title="Select date"
          >
            <IonIcon icon={calendar} />
          </IonButton>
        </div>
      </Fragment>
    );
  };

  useEffect(() => {
    setSelectedDate(selectedAbsenceDate);
  }, [selectedAbsenceDate]);

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { myRoomList, myRoomListLoaded } = useRoomResources();
  const {
    roomTimetable,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    roomTimetableLoaded,
    updateRoomTimetables,
    refreshRoomTimetable,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    isLoading,
  } = useRoomTimetable(selectedDate);

  const {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    lessonSaving,
    updateLesson,
    createLesson,
    isUpdateError,
    updateError,
    clearErrors,
  } = useLessonDetail("");

  const view: MbscEventcalendarView = useMemo(() => {
    return {
      timeline: {
        type: "day",
        startDay: 1,
        endDay: 5,
        startTime: "07:00",
        endTime: "22:00",
        timeCellStep: 60,
        timeLabelStep: 60,
        currentTimeIndicator: true,
        weekNumbers: false,
      },
    };
  }, []);

  useEffect(() => {
    if (isUpdateError && updateError) {
      console.log("updateError: ", updateError);
      displayMsg(updateError, { type: "danger" });
      clearErrors();
      refreshRoomTimetable();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateError, isUpdateError]);

  const updateLessonDetail = useCallback(
    async (newLesson: LessonDetail) => {
      const { _id } = newLesson;

      // If there is no lesson id, then create a new lesson
      if (!_id) {
        // Make sure we have a Class.CalendarDate based on the newLesson.LessonStartTime.
        // Convert to YYYY-MM-DD format if it is not already.  This is required for the createLesson call.
        if (!newLesson?.Class?.CalendarDate) {
          // Create newLesson.Class.CalendarDate if it is not already set.
          if (newLesson && newLesson.LessonStartTime) {
            if (!newLesson.Class) {
              newLesson.Class = {};
            }
            newLesson.Class.CalendarDate = dateAndTime.format(new Date(newLesson.LessonStartTime), "YYYY-MM-DD", false);
          }
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const res = await createLesson(newLesson);
        return;
      }

      // Otherwise update the existing lesson
      updateLesson(newLesson);
    },
    [createLesson, updateLesson]
  );

  const onEventUpdate = (args: any) => {
    const oldEvent = args.oldEvent;
    const newEvent = args.event;
    let newTimetable = [...roomTimetable];

    // if the old event room is same as new event room, then do nothing
    if (oldEvent.resource === newEvent.resource) {
      return false;
    }

    if (newEvent.event === true) {
      displayMsg("Can't do room swaps on events at the moment", {
        type: "warning",
        position: "middle",
      });
      return false;
    }

    // Find the event in the timetable list and increment the time by 1 hour
    const eventIndex = roomTimetable.findIndex(
      (event: any) => event._id === oldEvent._id
    );

    if (eventIndex > -1) {
      let evt = { ...newTimetable[eventIndex] };
      evt.start = oldEvent.start;
      evt.end = oldEvent.end;
      evt.resource = newEvent.resource;
      newTimetable[eventIndex] = evt;

      updateRoomTimetables(newTimetable);
    }
  };

  /**
   * Room has been updated on the UI, now persist the change to the database
   * @param args
   */
  const onEventUpdated = (args: any) => {
    console.log("onEventUpdated: ", args);
    const oldEvent = args.oldEvent;
    const newEvent = args.event;

    // Find the event in the timetable list and increment the time by 1 hour
    const eventIndex = roomTimetable.findIndex(
      (event: any) => event._id === oldEvent._id
    );
    if (eventIndex > -1) {
      const oldRoomId = oldEvent.resource;
      let oldRoomName = "Unknown";
      const newRoomId = newEvent.resource;
      let newRoomName = "Unknown";

      console.log("OldRoomId: ", oldRoomId, "NewRoomId: ", newRoomId);

      // Find the new room in the room list
      let roomIndex = myRoomList.findIndex(
        (room: any) => room?.RoomId === newRoomId
      );
      if (roomIndex > -1) {
        newRoomName = myRoomList[roomIndex].name;
      }

      // Find the new old room in the room list
      roomIndex = myRoomList.findIndex(
        (room: any) => room?.RoomId === oldRoomId
      );
      if (roomIndex > -1) {
        oldRoomName = myRoomList[roomIndex].name;
      }

      const startTime = oldEvent.start.replace("Z", "");
      const endTime = oldEvent.end.replace("Z", "");

      try {
        // Update the room on the server
        updateLessonDetail({
          _id: oldEvent.LessonId,
          Class: {
            PeriodClassId: oldEvent.PeriodClassId,
            PeriodId: oldEvent.PeriodId,
            ClassId: oldEvent.ClassId,
          },
          LessonStartTime: startTime,
          LessonEndTime: endTime,
          Room: {
            ForeignKey: oldRoomId,
            Short: oldRoomName,
            Swap: {
              ForeignKey: newRoomId,
              Short: newRoomName,
              Reason: "Room Swap",
            },
          },
        });
      } catch (err) {
        console.error("Error updating lesson: ", err);
        refreshRoomTimetable();
        return false;
      }
    }
  };

  const onSelectedDateChange = (args: any) => {
    console.log("onSelectedDateChange: ", args);
    // Format args.date to YYYY-MM-DD
    let d = new Date(args.date);
    let formattedDate = dateAndTime.format(d, "YYYY-MM-DD", false);
    setSelectedDate(formattedDate);
    // eslint-disable-next-line @typescript-eslint/no-unused-expressions
    setSelectedAbsenceDate && setSelectedAbsenceDate(formattedDate);
  };

  /**
   * Calendar Popup for room swap
   */
  const [isOpen, setOpen] = useState<boolean>(false);
  const [isEdit, setEdit] = useState<boolean>(false);
  const [headerText, setHeader] = useState<string>("");
  const [swapReason, setSwapReason] = useState<string>("");
  const [swapRoom, setSwapRoom] = useState<string>("");
  const [selectedEvent, setSelectedEvent] = useState<any>(null);

  const responsivePopup = {
    medium: {
      display: "center" as const,
      width: 400,
      fullScreen: false,
      touchUi: false,
      showOverlay: false,
    },
  };

  const saveEvent = useCallback(async () => {
    const newEvent = {
      room: swapRoom,
      reason: swapReason,
      event: selectedEvent,
    };
    if (isEdit && swapRoom) {
      // update the event in the list
      console.log("saveEvent - isEdit: ", newEvent);
      // Remove trailing 'Z' from the selectedEvent.start date if it exists
      const startTime = selectedEvent.start.replace("Z", "");
      const endTime = selectedEvent.end.replace("Z", "");

      await updateLessonDetail({
        _id: selectedEvent.LessonId,
        Class: {
          PeriodClassId: selectedEvent.PeriodClassId,
          PeriodId: selectedEvent.PeriodId,
          ClassId: selectedEvent.ClassId,
        },
        LessonStartTime: startTime,
        LessonEndTime: endTime,
        Room: {
          ForeignKey: selectedEvent.resource,
          Short: selectedEvent.resourceName,
          Swap: {
            ForeignKey: swapRoom,
            Reason: swapReason,
          },
        },
      });
      refreshRoomTimetable();
    } else {
      // add the new event to the list
      console.log("saveEvent - !isEdit: ", newEvent);
    }
    // close the popup
    setOpen(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, swapReason, swapRoom, selectedEvent]);

  const loadPopupForm = useCallback((event: any) => {
    setSwapRoom("");
    setSwapReason("");
    setSelectedEvent(event);
  }, []);

  // handle popup form changes
  const reasonChange = useCallback((ev: any) => {
    setSwapReason(ev?.target?.value);
  }, []);

  const swapRoomChange = useCallback((ev: any) => {
    setSwapRoom(ev.value);
  }, []);

  // scheduler options
  const onEventClick = useCallback(
    (args: any) => {
      const event = args.event;
      console.log("onEventClick: ", event);
      const title = event?.title;
      const course = event?.CourseName;

      if (event.event === true) {
        displayMsg("Can't do room swaps on events at the moment", {
          type: "warning",
          position: "middle",
        });
        return;
      }
      setHeader(
        "<div>" +
        title +
        " - " +
        course +
        '</div><div class="room-edit-header">' +
        formatDate("DDDD", new Date(event.start)) +
        ", " +
        formatDate("DD MMMM YYYY", new Date(event.start)) +
        "</div>"
      );
      setEdit(true);

      // fill popup form with event data
      loadPopupForm(event);
      setOpen(true);
    },
    [loadPopupForm]
  );

  // popup options
  const popupButtons: any = useMemo(() => {
    if (isEdit) {
      return [
        "cancel",
        {
          handler: () => {
            saveEvent();
          },
          keyCode: "enter",
          text: "Save",
          cssClass: "mbsc-popup-button-primary",
        },
      ];
    } else {
      return [
        "cancel",
        {
          handler: () => {
            saveEvent();
          },
          keyCode: "enter",
          text: "Add",
          cssClass: "mbsc-popup-button-primary",
        },
      ];
    }
  }, [isEdit, saveEvent]);

  const onClose = useCallback(() => {
    if (!isEdit) {
      // refresh the list, if add popup was canceled, to remove the temporary event
      console.log("onClose");
    }
    setOpen(false);
  }, [isEdit]);

  const renderEvent = useCallback((data: any) => {
    // if (data.allDay) {
    //   return (
    //     <div
    //       style={{ background: cat.color }}
    //       className="md-custom-event-allday-title"
    //     >
    //       {data.title}
    //     </div>
    //   );
    // } else {
    return (
      <div
        title={`${data.title}, ${data?.original?.CourseName ?? ""}, ${data?.original?.TeacherName ?? ""
          } : ${data.start} - ${data.end}`}
      >
        <div className="mbsc-schedule-event-background mbsc-timeline-event-background mbsc-ios"></div>
        <div aria-hidden="true" className="mbsc-schedule-event-inner mbsc-ios">
          <div className="mbsc-schedule-event-title mbsc-ios">{data.title}</div>
          <div className="mbsc-schedule-event-range mbsc-ios">
            {`${data?.original?.TeacherName ?? ""}`}
          </div>
          <div className="mbsc-schedule-event-subtitle mbsc-ios">
            {data.start} - {data.end}
          </div>
        </div>
      </div>
      // </div>
    );
  }, []);

  const renderRoomResource = (resource: any) => {
    return (
      <div
        className={"resource-room" + (resource?.matches ? " room-match" : "")}
      >
        <div className="room-name">{resource.name}</div>
        <IonIcon
          className="room-icon"
          icon={resource?.BuildingId ? businessOutline : homeOutline}
          size="small"
        />
      </div>
    );
  };

  // Get building/room search functions
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [searchBuildingsString, setSearchBuildingsString] =
    useState<string>("");
  const [searchRoomsString, setSearchRoomsString] = useState<string>("");

  const onSearchRoomsChange = (e: any) => {
    setSearchRoomsString(e?.detail?.value || "");
  };

  const resetRoomSearch = () => {
    setSearchRoomsString("");
    setFilteredBuildings([]);
    // Remove matches from all buildings/rooms
    myRoomList?.forEach((bld: BuildingDetail) => {
      bld["matches"] = false;
      if (bld?.children) {
        bld.children.forEach((rm: RoomDetail) => {
          rm["matches"] = false;
        });
      }
    });
  };

  const [filteredBuildings, setFilteredBuildings] = useState<any[]>([]);

  useEffect(() => {
    if (searchBuildingsString) {
      const filteredBuildings = myRoomList.filter(
        (building: BuildingDetail) =>
          building?.Building?.toLowerCase().includes(
            searchBuildingsString.toLowerCase()
          ) ||
          building?.BuildingShort?.toLowerCase().includes(
            searchBuildingsString.toLowerCase()
          )
      );
      setFilteredBuildings(filteredBuildings);
    } else {
      setFilteredBuildings([]);
    }
  }, [searchBuildingsString, myRoomList]);

  useEffect(() => {
    // If the search string is not empty, filter the list of rooms
    // For each item in myRoomList, find the top level items that match on either
    // "name" or have children that match on "name"
    if (searchRoomsString) {
      const filteredBuildings = myRoomList.filter(
        (building: BuildingDetail) => {
          let match = building?.name
            ?.toLowerCase()
            .includes(searchRoomsString.toLowerCase());
          if (!match && building?.children?.length) {
            match = building?.children?.some((room: RoomDetail) => {
              let roomMatch = room?.name
                ?.toLowerCase()
                .includes(searchRoomsString.toLowerCase());
              if (roomMatch) {
                // Find all the children that match on name and set the attribute "matches" to true
                // room["matches"] = true;
              }
              return roomMatch;
            });
            if (match) {
              // Find all the children that match on name and set the attribute "matches" to true
              building?.children?.forEach((room: RoomDetail) => {
                if (
                  room?.name
                    ?.toLowerCase()
                    .includes(searchRoomsString.toLowerCase())
                ) {
                  room["matches"] = true;
                }
              });
            }
          }
          if (match) {
            building["matches"] = true;
          }
          if (match && building?.children?.length && building?.collapsed) {
            building.collapsed = false;
          }
          return match;
        }
      );
      setFilteredBuildings(filteredBuildings);
    }
  }, [searchRoomsString, myRoomList]);

  return (
    <PymblePage cssClass="room-swap-page">
      <Eventcalendar
        theme="ios"
        themeVariant="light"
        className="room-swap-calendar"
        clickToCreate={false}
        dragToCreate={false}
        dragToMove={true}
        dragToResize={false}
        view={view}
        data={roomTimetable}
        resources={filteredBuildings.length ? filteredBuildings : myRoomList}
        selectedDate={selectedDate}
        onEventUpdate={onEventUpdate}
        onEventUpdated={onEventUpdated}
        onSelectedDateChange={onSelectedDateChange}
        onEventClick={onEventClick}
        renderResource={renderRoomResource}
        renderHeader={myCalendarHeader}
        renderScheduleEvent={renderEvent}
      />

      <Popup
        display="bottom"
        fullScreen={true}
        contentPadding={false}
        headerText={headerText}
        buttons={popupButtons}
        isOpen={isOpen}
        onClose={onClose}
        responsive={responsivePopup}
        cssClass="room-swap-popup"
      >
        <CoverRoomSelect onChange={swapRoomChange} />
        <div className="mbsc-form-group">
          <Textarea label="Reason" value={swapReason} onChange={reasonChange} />
        </div>
      </Popup>
    </PymblePage>
  );
};

export default RoomSwap;
